/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import React, { useState } from 'react';
import { Button, FormGroup, Label, Modal, ModalBody, ModalHeader } from 'reactstrap';
import AssuranceModalCreate from './assurance-modal-create';
import { SingleValue } from 'react-select';
import { getSurveyAssurancePortfolios } from '../../actions/assurance';
import { AssurancePortfolio } from '../../types/assurance';
import { optionPortfolioType } from '../../utils/portfolio';
import { DATE, formatDate } from '../../utils/date';
import { naturalSort } from '../../utils';
import { generateUrl } from '../../routes/util';
import { useHistory } from 'react-router-dom';
import { ROUTES } from '../../constants/routes';
import { useAppDispatch } from '../../reducers';
import { addSiteAlert, SiteAlertColors } from '../../slice/siteAlertsSlice';
import './assurance-modal.scss';
import { Loader } from '@g17eco/atoms/loader';
import { Option, SelectFactory, SelectTypes } from '@g17eco/molecules/select/SelectFactory';

export interface AssuranceModalProps {
  toggle: (submitted: boolean) => void;
  isOpen: boolean;
  initiativeId: string;
  surveyId: string;
}

export const AssuranceModal = (props: AssuranceModalProps) => {
  const { toggle, isOpen, initiativeId, surveyId } = props;
  const [existingAssurances, setExistingAssurances] = useState<AssurancePortfolio[]>([]);
  const [selectedAssurance, setSelectedAssurance] = useState<Option | undefined>(undefined);
  const [isCreateView, setIsCreateView] = useState(false);
  const [loading, setLoading] = useState(true);

  const history = useHistory();
  const dispatch = useAppDispatch();

  React.useEffect(() => {
    getSurveyAssurancePortfolios(surveyId)
      .then((data: { assurances: AssurancePortfolio[] }) => {
        setExistingAssurances(data.assurances);
        setIsCreateView(data.assurances.length === 0)
        setLoading(false);
      }).catch(() => {
        setExistingAssurances([]);
        setLoading(false);
        dispatch(addSiteAlert({
          content: 'Error. Could not load existing assurance tracker list. Please refresh and try again',
          timeout: 10000,
          color: SiteAlertColors.Danger
        }));
      });
  }, [surveyId, dispatch]);

  const assuranceOptions: Option[] = existingAssurances.map(a => {
    const type = optionPortfolioType.find(o => o.value === a.portfolioType) ?? optionPortfolioType[0];
    const label = `${a.organization.name} (${formatDate(a.created, DATE.DEFAULT_SPACES)}) - ${type.label}`;
    return {
      value: a._id,
      label: label
    }
  }).sort((a, b) => naturalSort(a.label, b.label));

  React.useEffect(() => {
    if (selectedAssurance === undefined && assuranceOptions.length > 0) {
      setSelectedAssurance(assuranceOptions[0]);
    }
  }, [selectedAssurance, assuranceOptions]);

  const handleOnChange = (value: SingleValue<Option> | null) => {
    const assuranceId = value?.value;
    const assurance = assuranceOptions.find(e => e.value === assuranceId);
    setSelectedAssurance(assurance);
  }

  const handleViewEdit = (assuranceId: string | undefined | null) => {
    if (!assuranceId) {
      return;
    }

    const url = generateUrl(ROUTES.COMPANY_TRACKER_ASSURANCE,
      {
        initiativeId,
        surveyId,
        assuranceId
      });
    history.push(`${url}${history.location.search}`);
    toggle(false);
  }

  const renderBody = () => {

    if (loading) {
      return <div style={{ minHeight: '200px' }}><Loader /></div>;
    }

    if (isCreateView) {
      return <AssuranceModalCreate
        toggle={(submitted: boolean) => submitted ? toggle(true) : toggle(false)}
        initiativeId={initiativeId}
        surveyId={surveyId}
      />
    }

    return <div>
      <FormGroup>
        <Label>Existing assurance tracker</Label>
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          placeholder={loading ? 'Loading...' : ''}
          isDisabled={assuranceOptions.length === 0}
          className='w-100'
          isMulti={false}
          onChange={handleOnChange}
          value={selectedAssurance ?? null}
          isSearchable={true}
          options={assuranceOptions}
        />
      </FormGroup>
      <div className='mt-3 text-right'>
        <Button onClick={() => handleViewEdit(selectedAssurance?.value)}
          disabled={assuranceOptions.length === 0}>View</Button>
      </div>
      <div className='mt-6 text-right'>
        <Button color='primary' onClick={() => setIsCreateView(true)}>
          <i className='fa fa-plus mr-2' />Create new assurance
        </Button>
      </div>
    </div>;
  };

  return (
    <Modal isOpen={isOpen} toggle={() => toggle(false)} backdrop='static' className='assurance-modal'>
      <ModalHeader toggle={() => toggle(false)}>Manage Assurance</ModalHeader>
      <ModalBody>{renderBody()}</ModalBody>
    </Modal>
  );
};
